import { buildScoreExplanation } from './score-explanation';
import type { SearchHit, SearchScoring } from '../types';

/**
 * The rate panel of the result-detail dialog (OMA-639), as a list of meters.
 *
 * The design draws this panel as `base × weight = contribution`, summing to the
 * final percentage. The pipeline computes no such weighted score: ranking is
 * the engine's hybrid of cosine distance and `text_match`, and OCR confidence
 * takes no part in it at all. So this module keeps the design's SHAPE — a row
 * per factor, a bar, a final line — and fills it only with numbers the backend
 * actually reported:
 *
 *   - `percent` is non-null ONLY for a factor genuinely on a 0–100 scale. The
 *     keyword factor has none (`text_match` is a packed integer), so it renders
 *     as counts with no bar rather than as an invented percentage.
 *   - `finalPercent` IS one of the meters — never a sum computed here — and
 *     `finalSourceKind` names which one, so the panel can say so out loud. It
 *     is the RANK row, not the meaning-closeness row: the rate is whatever put
 *     the card where it is, and the cosine never did that — which is also why
 *     the card's own ring, showing that cosine, carries a label naming it.
 *   - OCR confidence is real and worth showing, but `rankingFactor: false`
 *     marks it as describing the extraction, not the ordering.
 *
 * Pure and framework-free: returns i18n KEYS and values, never resolved
 * strings. Pinned by `tests/unit/search-rate-meters.test.ts`.
 */

export type RateMeterKind = 'rank' | 'semantic' | 'keyword' | 'ocr';

export interface RateMeter {
  kind: RateMeterKind;
  /** 0–100 when the factor is on that scale; null when it simply is not. */
  percent: number | null;
  /** Message key under `search.explain.meter.*` for the row's label. */
  labelKey: string;
  /** Message key under `search.explain.meter.*` for the row's value text. */
  valueKey: string;
  /** Interpolation values for `valueKey`. Only keys with real data. */
  values: Record<string, number>;
  /** False for a factor that did NOT take part in the ranking. */
  rankingFactor: boolean;
}

export interface RateMeterSummary {
  meters: RateMeter[];
  /** The rate the card shows, or null when this result carries none. */
  finalPercent: number | null;
  /** Which meter the final rate is. Null when there is no rate. */
  finalSourceKind: RateMeterKind | null;
}

/** Keep a bar inside its track without rewriting the number it came from. */
function clampPercent(value: number): number {
  return Math.max(0, Math.min(100, value));
}

export function buildRateMeters(
  hit: SearchHit,
  scoring?: SearchScoring | null
): RateMeterSummary {
  const breakdown = hit.score_breakdown ?? null;

  if (!breakdown) {
    return { meters: [], finalPercent: null, finalSourceKind: null };
  }

  const meters: RateMeter[] = [];

  // The rate is the value that decided the order, taken from the server as-is
  // — see `buildScoreExplanation` — so this panel and the explanation beside
  // it cannot drift apart. It is deliberately NOT the card's ring: that shows
  // the labelled cosine, which is present far more often and orders nothing.
  const { percent } = buildScoreExplanation(hit, scoring);

  if (percent != null) {
    meters.push({
      kind: 'rank',
      percent: clampPercent(percent),
      labelKey: 'meter.rank',
      valueKey: 'meter.rankValue',
      values: { percent },
      rankingFactor: true
    });
  }

  const similarity = breakdown.semantic?.similarity ?? null;

  if (similarity != null) {
    meters.push({
      kind: 'semantic',
      percent: clampPercent(similarity),
      labelKey: 'meter.semantic',
      valueKey: 'meter.semanticValue',
      values: { percent: similarity },
      rankingFactor: true
    });
  }

  const keyword = breakdown.keyword ?? null;
  if (keyword) {
    // `tokens_matched` is the engine's count; falling back to the number of
    // tokens it highlighted is the same fact read off another field. An empty
    // highlight list is "not reported", not "zero matched".
    const highlighted = keyword.matched_tokens?.length ?? 0;
    const tokens =
      keyword.tokens_matched ?? (highlighted > 0 ? highlighted : null);
    const fields = keyword.fields_matched ?? keyword.fields?.length ?? null;

    const values: Record<string, number> = {};
    if (tokens != null) values.tokens = tokens;
    if (fields != null) values.fields = fields;

    if (tokens != null || fields != null) {
      meters.push({
        kind: 'keyword',
        // `text_match` is a packed integer whose magnitude means nothing
        // outside a comparison — there is no bar to draw for it.
        percent: null,
        labelKey: 'meter.keyword',
        valueKey:
          tokens != null
            ? fields != null
              ? 'meter.keywordValue'
              : 'meter.keywordTokensOnly'
            : 'meter.keywordFieldsOnly',
        values,
        rankingFactor: true
      });
    }
  }

  if (hit.ocr_confidence != null) {
    meters.push({
      kind: 'ocr',
      percent: clampPercent(hit.ocr_confidence),
      labelKey: 'meter.ocr',
      valueKey: 'meter.ocrValue',
      values: { percent: hit.ocr_confidence },
      // The extraction's own confidence. The engine never ranked by it, and
      // showing it inside a rate panel without saying so would imply it did.
      rankingFactor: false
    });
  }

  return {
    meters,
    finalPercent: percent,
    // The rank row is pushed above whenever there is a rate, so this never
    // points at a row the panel did not render.
    finalSourceKind: percent != null ? 'rank' : null
  };
}
